import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import { CenteredWrapper } from '../../UIControls/centered-wrapper';
import DownloadUtils from '../../../utils/download-utils';
import UserApi from '../../../rest-api/user-api';
import { useNavigate } from 'react-router-dom';
import UploadUtils from '../../../utils/UploadUtils';
import { Auth } from 'aws-amplify';
import SuccessDialog from '../../UIControls/api-button/SuccessDialog';
import { useUnsavedChanges } from '../UnsavedChangesContext';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import SettingsUser from './settings-user';
import SettingsClinic from './settings-clinic';
import UnsavedChangesModal from '../../UIControls/UnsavedChangesModal';
import SettingsPassword from './settings-password';
import SettingsBilling from './settings-billing';
import SettingsDeleteAccount from './settings-delete-account';
import SettingsSubscription from './settings-subscription';
import SettingsIntegrations from './settings-integrations';
import CTAUpgrade from '../../UIControls/CTAUpgrade';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import PinkGradientButton from '../../UIControls/PinkGradientButton';
import SettingsMultiUserAdmin from './settings-multiuser-admin';
import { getCTAUpgradeSettingsImageUrl, getCTAUpgradeImageUrl, practiceOrClinicText } from '../../../config';

const SETTINGS_SECTION_TITLES = [
    'Personal details',
    'Practice details',
    'Password',
    'Integrations',
    'Billing',
    'Subscription',
];

export function SettingsScreen() {
    const theme = useTheme();
    const appStateStore = useContext(StoreContext);
    const { authStore, userDataStore } = useContext(StoreContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const idToken = authStore.idToken;
    const [clinicianData, setClinicianData] = useState(null);
    const [clinicImage, setClinicImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
    const [passwordChangeError, setPasswordChangeError] = useState('');
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [isDeleteLogoDialogOpen, setIsDeleteLogoDialogOpen] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [isVerificationDialogOpen, setIsVerificationDialogOpen] = useState(false);
    const [pendingClinicDataUpdate, setPendingClinicDataUpdate] = useState(null);
    const [isSuccessEmailDialogOpen, setIsSuccessEmailDialogOpen] = useState(false);
    const [trialExpiryTime, setTrialExpiryTime] = useState(null);
    const [showClinicSettings, setShowClinicSettings] = useState(false);
    const [upgradeCTAOpenProfessional, setUpgradeCTAOpenProfessional] = useState(false);
    const [upgradeCTAOpenClinic, setUpgradeCTAOpenClinic] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const { hasUnsavedChanges, setHasUnsavedChanges, onConfirmNavigation, onCancelNavigation, showPrompt } =
        useUnsavedChanges();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState('');
    const rightSectionsScrollRef = useRef(null);
    const sectionRefs = useRef({});
    const [activeSection, setActiveSection] = useState('Personal details');
    const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';
    const settingsSectionTitles = SETTINGS_SECTION_TITLES;

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        if (hasUnsavedChanges) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    useEffect(() => {
        fetchClinicianData();
    }, []);

    useEffect(() => {
        if (loading || isMobile || showClinicSettings) return;
        const container = rightSectionsScrollRef.current;
        if (!container) return;

        const onScroll = () => {
            const visibleSectionOrder = [
                'Personal details',
                'Practice details',
                'Password',
                'Integrations',
                'Billing',
                'Subscription',
            ];
            const threshold = 16;
            let current = visibleSectionOrder[0];

            // Ensure last section becomes active at the very bottom, even if its top never crosses threshold.
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
                setActiveSection('Subscription');
                return;
            }

            const containerRect = container.getBoundingClientRect();
            visibleSectionOrder.forEach((title) => {
                const node = sectionRefs.current[title];
                if (!node) return;
                const sectionTopRelativeToContainer = node.getBoundingClientRect().top - containerRect.top;
                if (sectionTopRelativeToContainer <= threshold) {
                    current = title;
                }
            });
            setActiveSection(current);
        };

        onScroll();
        container.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('scroll', onScroll, { passive: true });

        const resizeObserver = new ResizeObserver(onScroll);
        resizeObserver.observe(container);

        Object.values(sectionRefs.current).forEach((node) => {
            if (node) resizeObserver.observe(node);
        });

        return () => {
            container.removeEventListener('scroll', onScroll);
            window.removeEventListener('scroll', onScroll);
            resizeObserver.disconnect();
        };
    }, [
        loading,
        isMobile,
        showClinicSettings,
        clinicianData?.subscriptionData?.stripeCustomerId,
        clinicianData?.subscriptionData?.accountType,
    ]);

    useEffect(() => {
        if (newEmail) {
            setClinicianData((prevData) => ({
                ...prevData,
                emailAddress: newEmail,
            }));
        }
    }, [newEmail]);

    const fetchClinicianData = async () => {
        setLoading(true);
        try {
            const clinicianDataResponse = await UserApi.getClinicianData(authStore.idToken);
            setNewEmail(clinicianDataResponse.userData.emailAddress);
            if (clinicianDataResponse) {
                clinicianDataResponse.clinicData = clinicianDataResponse.clinicData || {};
                setClinicianData(clinicianDataResponse);
                console.warn('Clinician data fetched and component state updated successfully.', clinicianDataResponse);

                // Placeholder end date in epoch to find days until expiry
                const { subscriptionEndDate } = clinicianDataResponse.subscriptionData || {};

                if (subscriptionEndDate) {
                    const currentTime = Date.now();
                    const subscriptionEndTimeInMilliseconds = subscriptionEndDate;
                    const differenceInMilliseconds = subscriptionEndTimeInMilliseconds - currentTime;
                    const millisecondsInOneDay = 24 * 60 * 60 * 1000;
                    const differenceInDays = differenceInMilliseconds / millisecondsInOneDay;

                    setTrialExpiryTime(Math.floor(differenceInDays));
                    console.warn('Expiry in days:', Math.floor(differenceInDays));
                }

                // Initialize formattedData with properties, ensuring clinicData defaults to {}
                const clinicData = clinicianDataResponse.clinicData || {};

                let formattedData = {
                    firstName: clinicianDataResponse.userData.firstName,
                    lastName: clinicianDataResponse.userData.lastName,
                    emailAddress: clinicianDataResponse.userData.emailAddress,
                    mobile: clinicianDataResponse.userData.mobile || '',
                    jobTitle: clinicianDataResponse.userData.jobTitle || '',
                    yearsExperience: clinicianDataResponse.userData.yearsExperience || '',
                    clinicName: clinicData.clinicName || '',
                    clinicNumber: clinicData.clinicNumber || '',
                    clinicSize: clinicData.clinicSize || '',
                    clinicType: clinicData.clinicType || '',
                    clinicImageId: '',
                    showLogoInHandouts: clinicianDataResponse.showLogoInHandouts,
                    handouts: JSON.stringify(clinicianDataResponse.handouts),
                    recentSearches: clinicianDataResponse.recentSearches || [],
                    subscriptionData: clinicianDataResponse.subscriptionData || {},
                    integrations: clinicianDataResponse.integrations || null,
                };

                if (clinicData.clinicImageId) {
                    const payload = {
                        files: [{ method: 'get', key: clinicData.clinicImageId }],
                        contentCategory: 'clinic_image',
                    };

                    const clinicImageURL = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                    if (clinicImageURL && clinicImageURL.getUrls) {
                        console.warn('Clinic image found:', clinicImageURL);
                        setClinicImage(clinicImageURL.getUrls[0].presignedUrl);
                        let clinicImageBase64 = await DownloadUtils.downloadImageFromPresignedUrl(
                            clinicImageURL.getUrls[0].presignedUrl,
                        );
                        formattedData.clinicImageId = clinicImageBase64.base64data;
                    }
                } else {
                    setClinicImage('');
                }

                console.warn(formattedData);
                userDataStore.setUserData(formattedData);
            }
        } catch (error) {
            console.error('Failed to fetch clinician data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSignout = async () => {
        await authStore.logout();
        await appStateStore.clearUserStores();
        localStorage.clear();
    };

    const handleFileChange = async (e) => {
        if (e.target.files.length > 0) {
            const file = e.target.files[0];

            if (file.size > 2 * 1024 * 1024) {
                alert('File size exceeds the allowed maximum of 2MB. Please select another image.');
                return;
            }

            const objectURL = URL.createObjectURL(file);

            const image = new Image();
            image.src = objectURL;
            image.onload = async () => {
                if (image.width > 700 || image.height > 700) {
                    alert(
                        'Image dimensions exceed the allowed maximum of 700x700 pixels. Please select another image.',
                    );
                    URL.revokeObjectURL(objectURL);
                } else {
                    setSelectedFile(file);
                    await uploadClinicImage(file);
                }
            };
            image.onerror = () => {
                alert('There was an error loading the image. Please select another image.');
                URL.revokeObjectURL(objectURL);
            };
        }
    };

    const createFileObject = (file) => {
        const extension = file.name.split('.').pop();
        const baseKey = `${userDataStore.userId}/profile-image`;
        console.warn('file object:', file);
        return {
            key: `${baseKey}.${extension}`,
            fileContentType: file.type,
            fileSize: file.size,
        };
    };

    const uploadClinicImage = async (selectedFile) => {
        if (!selectedFile) return;

        const fileObject = createFileObject(selectedFile);
        try {
            const response = await UserApi.createPresignedImagePutUrl(idToken, fileObject);
            if (response && response.putUrls) {
                const { presignedUrl, fields } = response.putUrls[0];
                const uploadedClinicImage = await UploadUtils.uploadFile(fields, selectedFile, presignedUrl);

                if (uploadedClinicImage) {
                    assignClinicImageToUser(fileObject);
                }
            }
        } catch (error) {
            console.error('Failed to upload file:', error);
        } finally {
            setSelectedFile(null);
        }
    };

    const assignClinicImageToUser = async (fileObject) => {
        const payload = {
            firstName: clinicianData.userData.firstName,
            lastName: clinicianData.userData.lastName,
            emailAddress: clinicianData.userData.emailAddress,
            mobile: clinicianData.userData.mobile || '',
            jobTitle: clinicianData.userData.jobTitle || '',
            yearsExperience: clinicianData.userData.yearsExperience || '',
            clinicName: clinicianData.clinicData.clinicName,
            clinicNumber: clinicianData.clinicData.clinicNumber || '',
            clinicSize: clinicianData.clinicData.clinicSize || '',
            clinicType: clinicianData.clinicData.clinicType || '',
            showLogoInHandouts: clinicianData.showLogoInHandouts,
            clinicImageId: fileObject.key,
        };
        console.warn('assignClinicImageToUser payload:', payload);
        await UserApi.updateClinicianData(authStore.idToken, payload);
        // update indexDB then reload view to show updated image
        fetchClinicianData();
    };

    const removeClinicImage = async (fileObject) => {
        console.warn('removeClinicImage');
        const payload = {
            firstName: clinicianData.userData.firstName,
            lastName: clinicianData.userData.lastName,
            emailAddress: clinicianData.userData.emailAddress,
            mobile: clinicianData.userData.mobile || '',
            jobTitle: clinicianData.userData.jobTitle || '',
            yearsExperience: clinicianData.userData.yearsExperience || '',
            clinicName: clinicianData.clinicData.clinicName,
            clinicNumber: clinicianData.clinicData.clinicNumber || '',
            clinicSize: clinicianData.clinicData.clinicSize || '',
            clinicType: clinicianData.clinicData.clinicType || '',
            clinicImageId: '',
            showLogoInHandouts: clinicianData.showLogoInHandouts,
        };
        console.warn('removeClinicImage payload:', payload);
        await UserApi.updateClinicianData(authStore.idToken, payload);

        // reload view to show updated image
        fetchClinicianData();
    };

    const handleCloseDeleteLogoDialog = () => {
        setIsDeleteLogoDialogOpen(false);
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;

        // Unique case: handling combined Username field
        if (name === 'username') {
            const names = value.split(' ');
            const firstName = names[0] || '';
            const lastName = names.slice(1).join(' ') || '';

            setClinicianData((prevState) => ({
                ...prevState,
                userData: {
                    ...prevState.userData,
                    firstName,
                    lastName,
                },
            }));
        } else {
            const [parentKey, childKey] = name.split('.');

            setClinicianData((prevState) => ({
                ...prevState,
                [parentKey]: {
                    ...prevState[parentKey],
                    [childKey]: value,
                },
            }));
        }
        setHasUnsavedChanges(true);
    };

    const updateEmail = async (newEmail, clinicDataPayload) => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            await Auth.updateUserAttributes(user, { email: newEmail });
            const clinicDataPayloadWithEmail = { ...clinicDataPayload, emailAddress: newEmail };
            setPendingClinicDataUpdate(clinicDataPayloadWithEmail);
            setIsVerificationDialogOpen(true);
        } catch (error) {
            if (error.message.includes('An account with the given email already exists')) {
                setErrorDialogMessage('This email is already being used by another account.');
                setIsErrorDialogOpen(true);
            } else {
                console.error('Error updating email in Cognito:', error);
            }
        }
    };

    const verifyEmailWithCode = async () => {
        try {
            await Auth.verifyCurrentUserAttributeSubmit('email', verificationCode);
            console.warn('Email verified successfully');

            if (pendingClinicDataUpdate) {
                await performClinicDataUpdate(pendingClinicDataUpdate);
                setPendingClinicDataUpdate(null);
            }

            setIsVerificationDialogOpen(false);
            setIsSuccessEmailDialogOpen(true);
        } catch (error) {
            console.error('Error verifying email:', error);
        }
    };

    const updateClinicData = async () => {
        const clinicDataPayload = {
            firstName: clinicianData.userData.firstName,
            lastName: clinicianData.userData.lastName,
            emailAddress: clinicianData.userData.emailAddress,
            mobile: clinicianData.userData.mobile || '',
            jobTitle: clinicianData.userData.jobTitle || '',
            yearsExperience: clinicianData.userData.yearsExperience || '',
            clinicName: clinicianData.clinicData.clinicName,
            clinicNumber: clinicianData.clinicData.clinicNumber || '',
            clinicSize: clinicianData.clinicData.clinicSize || '',
            clinicType: clinicianData.clinicData.clinicType || '',
            clinicImageId: clinicianData.clinicData.clinicImageId,
            showLogoInHandouts: clinicianData.showLogoInHandouts,
        };

        console.warn('payload', clinicDataPayload);

        if (newEmail !== clinicianData.userData.emailAddress) {
            await updateEmail(newEmail, clinicDataPayload);
        } else {
            await performClinicDataUpdate(clinicDataPayload);
            setHasUnsavedChanges(false);
        }
    };

    const performClinicDataUpdate = async (clinicDataPayload) => {
        try {
            const updatedData = await UserApi.updateClinicianData(authStore.idToken, clinicDataPayload);
            console.warn('Clinic data update API response:', updatedData);
            fetchClinicianData(); // Refresh clinician data
        } catch (error) {
            console.error('Failed to update clinic data:', error);
        }
    };

    const scrollToSection = useCallback((sectionTitle) => {
        const sectionNode = sectionRefs.current[sectionTitle];
        const container = rightSectionsScrollRef.current;
        if (!sectionNode) return;

        if (isMobile || !container) {
            sectionNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveSection(sectionTitle);
            return;
        }

        // Compute robustly from viewport deltas to avoid offsetParent/margin drift.
        const containerRect = container.getBoundingClientRect();
        const sectionRect = sectionNode.getBoundingClientRect();
        const topGap = 8;
        const targetTop = Math.max(0, container.scrollTop + (sectionRect.top - containerRect.top) - topGap);
        container.scrollTo({ top: targetTop, behavior: 'smooth' });
        setActiveSection(sectionTitle);
    }, [isMobile]);

    useEffect(() => {
        if (loading || showClinicSettings) return;

        const requestedSection = location.state?.scrollToSection;
        if (requestedSection && SETTINGS_SECTION_TITLES.includes(requestedSection)) {
            const timerId = window.setTimeout(() => scrollToSection(requestedSection), 0);
            return () => window.clearTimeout(timerId);
        }

        if (location.state?.scrollToPracticeDetails) {
            const timerId = window.setTimeout(() => scrollToSection('Practice details'), 0);
            return () => window.clearTimeout(timerId);
        }

        return undefined;
    }, [
        loading,
        location.state?.scrollToPracticeDetails,
        location.state?.scrollToSection,
        scrollToSection,
        showClinicSettings,
    ]);

    return (
        <CenteredWrapper
            component="main"
            sx={{ zIndex: 1, position: 'relative', overflowX: 'hidden', background: '#F0F0FC' }}
        >
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : (
                <Box
                    sx={{ gap: 0, width: '100%', maxWidth: 1320, background: '#F0F0FC' }}
                    className="settings-container"
                >
                    <Box>
                        <Box style={{ textAlign: 'left', marginTop: '24px' }}>
                            <Box
                                sx={{
                                    position: { xs: 'static', md: 'sticky' },
                                    top: { md: 12 },
                                    zIndex: { md: 5 },
                                    background: '#F0F0FC',
                                    pb: { md: 2 },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    flexWrap: 'wrap',
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: '#000453',
                                        fontSize: '40px',
                                        fontWeight: 600,
                                        lineHeight: '40px',
                                        letterSpacing: '-1.6px',
                                    }}
                                >
                                    Manage your account
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: { xs: 1, md: 0 } }}>
                                    {userDataStore?.subscriptionData?.accountType === 'Clinic' && (
                                        <Box
                                            aria-label="account settings mode"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                background: '#FFFFFF',
                                                borderRadius: '58px',
                                                p: '4px',
                                                gap: '4px',
                                                flexShrink: 0,
                                                height: { xs: '46px', md: 'auto' },
                                            }}
                                        >
                                            {[
                                                { label: 'Admin', value: false },
                                                { label: 'Clinic', value: true },
                                            ].map((item) => {
                                                const selected = showClinicSettings === item.value;
                                                return (
                                                    <Box
                                                        key={item.label}
                                                        role="button"
                                                        aria-pressed={selected}
                                                        onClick={() => setShowClinicSettings(item.value)}
                                                        sx={{
                                                            borderRadius: '48px',
                                                            px: { xs: '14px', md: '20px' },
                                                            py: { xs: '9px', md: '10px' },
                                                            background: selected ? '#000453' : 'transparent',
                                                            color: selected ? '#FFFFFF' : '#000453',
                                                            fontFamily: 'Inter',
                                                            fontWeight: 500,
                                                            fontSize: { xs: '16px', md: '17px' },
                                                            lineHeight: { xs: '16px', md: '17px' },
                                                            letterSpacing: { xs: '-0.32px', md: '-0.35px' },
                                                            cursor: 'pointer',
                                                            userSelect: 'none',
                                                            transition: 'background 0.2s, color 0.2s',
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    {userDataStore?.subscriptionData?.trialActive && (
                                        <PinkGradientButton
                                            sx={{ height: 40, width: 99 }}
                                            onClick={() => navigate('./manage-subscription')}
                                        >
                                            Upgrade
                                        </PinkGradientButton>
                                    )}
                                </Box>
                            </Box>
                            {trialExpiryTime <= 30 && clinicianData?.subscriptionData?.accountType !== 'Free' && (
                                <Typography variant="subtitle" sx={{ mb: 2 }}>
                                    You have <strong> {trialExpiryTime} days</strong> left of your{' '}
                                    {clinicianData?.subscriptionData?.accountType}{' '}
                                    {clinicianData?.subscriptionData?.trialActive ? 'free trial' : ''} plan.
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    gap: { xs: 2, md: 3 },
                                    alignItems: { xs: 'flex-start', md: 'flex-start' },
                                }}
                            >
                                {!isMobile && !showClinicSettings && (
                                    <Box
                                        sx={{
                                            width: 280,
                                            background: '#fff',
                                            borderRadius: '30px',
                                            border: '1px solid rgba(102, 102, 102, 0.15)',
                                            overflow: 'hidden',
                                            position: 'sticky',
                                            top: 120,
                                            alignSelf: 'flex-start',
                                            minHeight: 'calc(100vh - 140px)',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height: 18,
                                                background: '#fff',
                                                borderBottom: '1px solid rgba(229,229,229,1)',
                                            }}
                                        />
                                        {settingsSectionTitles.map((item, index) => (
                                            <Box
                                                key={item}
                                                onClick={() => scrollToSection(item)}
                                                sx={{
                                                    px: 3,
                                                    py: 0,
                                                    minHeight: 78,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: '#000453',
                                                    fontWeight: 400,
                                                    fontSize: '24px',
                                                    fontStyle: 'normal',
                                                    lineHeight: '24px',
                                                    letterSpacing: '-0.48px',
                                                    backgroundColor: activeSection === item ? '#F0F0FC' : '#fff',
                                                    borderLeft:
                                                        activeSection === item
                                                            ? '2px solid #FF669B'
                                                            : '2px solid transparent',
                                                    borderBottom: '1px solid rgba(229,229,229,1)',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {item}
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                <Box
                                    ref={rightSectionsScrollRef}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        maxHeight: { xs: 'none', md: 'calc(100vh - 140px)' },
                                        overflowY: { xs: 'visible', md: 'auto' },
                                        pr: { md: 1 },
                                    }}
                                >
                                    {!showClinicSettings && (
                                        <>
                                            <Box
                                                ref={(node) => {
                                                    sectionRefs.current['Personal details'] = node;
                                                }}
                                            >
                                                <SettingsUser
                                                    clinicianData={clinicianData}
                                                    updateClinicData={updateClinicData}
                                                    handleChangeInput={handleChangeInput}
                                                    newEmail={newEmail}
                                                    setNewEmail={setNewEmail}
                                                    updateEmail={updateEmail}
                                                    layout="two-column"
                                                    sectionSx={{ mt: 0 }}
                                                />
                                            </Box>

                                            {clinicianData?.subscriptionData?.accountType !== 'Student' && (
                                                <Box
                                                    ref={(node) => {
                                                        sectionRefs.current['Practice details'] = node;
                                                    }}
                                                >
                                                    <SettingsClinic
                                                        setClinicianData={setClinicianData}
                                                        clinicianData={clinicianData}
                                                        clinicImage={clinicImage}
                                                        updateClinicData={updateClinicData}
                                                        handleChangeInput={handleChangeInput}
                                                        handleFileChange={handleFileChange}
                                                        handleSignout={handleSignout}
                                                        fileInputRef={fileInputRef}
                                                        setIsDeleteLogoDialogOpen={setIsDeleteLogoDialogOpen}
                                                        pendingClinicDataUpdate={pendingClinicDataUpdate}
                                                        setPendingClinicDataUpdate={setPendingClinicDataUpdate}
                                                        setHasUnsavedChanges={setHasUnsavedChanges}
                                                        idToken={idToken}
                                                        isMobile={isMobile}
                                                        passwordChangeError={passwordChangeError}
                                                        setPasswordChangeError={setPasswordChangeError}
                                                        isSuccessDialogOpen={isSuccessDialogOpen}
                                                        setIsSuccessDialogOpen={setIsSuccessDialogOpen}
                                                        isDeleteLogoDialogOpen={isDeleteLogoDialogOpen}
                                                        isVerificationDialogOpen={isVerificationDialogOpen}
                                                        setIsVerificationDialogOpen={setIsVerificationDialogOpen}
                                                        showPrompt={showPrompt}
                                                        onConfirmNavigation={onConfirmNavigation}
                                                        onCancelNavigation={onCancelNavigation}
                                                        hasUnsavedChanges={hasUnsavedChanges}
                                                        loading={loading}
                                                        setLoading={setLoading}
                                                        isFreeplan={
                                                            clinicianData?.subscriptionData?.accountType === 'Free'
                                                        }
                                                        onUpgradeClick={() => setUpgradeCTAOpenProfessional(true)}
                                                        isLogoUploadBlocked={
                                                            clinicianData?.subscriptionData?.accountType ===
                                                            'Professional'
                                                        }
                                                        onLogoUpgradeClick={() => setUpgradeCTAOpenClinic(true)}
                                                        layout="two-column"
                                                        sectionSx={{ mt: 0 }}
                                                    />
                                                </Box>
                                            )}

                                            <Box
                                                ref={(node) => {
                                                    sectionRefs.current['Password'] = node;
                                                }}
                                            >
                                                <SettingsPassword
                                                    isChangePasswordDialogOpen={isChangePasswordDialogOpen}
                                                    setIsChangePasswordDialogOpen={setIsChangePasswordDialogOpen}
                                                />
                                            </Box>

                                            <Box
                                                ref={(node) => {
                                                    sectionRefs.current['Integrations'] = node;
                                                }}
                                            >
                                                <SettingsIntegrations
                                                    clinicianData={clinicianData}
                                                    idToken={idToken}
                                                    onRefresh={fetchClinicianData}
                                                    isFreeplan={clinicianData?.subscriptionData?.accountType === 'Free'}
                                                    onUpgradeClick={() => setUpgradeCTAOpenProfessional(true)}
                                                    isMultiUser={isMultiUser}
                                                    inviteId={userDataStore?.userData?.inviteId}
                                                    emailAddress={userDataStore?.userData?.emailAddress}
                                                    currentUserId={userDataStore?.userId}
                                                />
                                            </Box>
                                        </>
                                    )}

                                    {showClinicSettings && <SettingsMultiUserAdmin clinicianData={clinicianData} />}

                                    {clinicianData?.subscriptionData?.trialActive &&
                                        clinicianData?.subscriptionData?.accountType !== 'Free' && (
                                            <CTAUpgrade
                                                open={true}
                                                title="Take the next step!"
                                                message="Your free trial is ending soon. Why not upgrade your subscription now and gain access to all of our features."
                                                buttonText="Upgrade plan"
                                                imageUrl={getCTAUpgradeSettingsImageUrl('trial', isMobile)}
                                                isMobile={isMobile}
                                            />
                                        )}

                                    {!clinicianData?.subscriptionData?.trialActive &&
                                        clinicianData?.subscriptionData?.accountType === 'Free' && (
                                            <CTAUpgrade
                                                open={true}
                                                title="Unlock unlimited sharing and more with just one click!"
                                                message="Upgrade your subscription to professional and access even more features"
                                                buttonText="Upgrade plan"
                                                imageUrl={getCTAUpgradeSettingsImageUrl('professional', isMobile)}
                                                isMobile={isMobile}
                                                benefits={[
                                                    'Unlimited digital sharing of all handouts',
                                                    'Unlimited digital sharing of all anatomy diagrams',
                                                    'Personalise with your clinic details',
                                                ]}
                                            />
                                        )}

                                    {!clinicianData?.subscriptionData?.trialActive &&
                                        clinicianData?.subscriptionData?.accountType === 'Student' && (
                                            <CTAUpgrade
                                                open={true}
                                                title="Go premium now!"
                                                message="Upgrade your subscription to professional and access even more features."
                                                buttonText="Upgrade plan"
                                                imageUrl={getCTAUpgradeSettingsImageUrl('professional', isMobile)}
                                                isMobile={isMobile}
                                            />
                                        )}

                                    {!clinicianData?.subscriptionData?.trialActive &&
                                        clinicianData?.subscriptionData?.accountType === 'Professional' && (
                                            <CTAUpgrade
                                                open={true}
                                                title="Add your team members!"
                                                message="Upgrade to our clinic plan and add up to 4 users whom can share their own handouts!"
                                                buttonText="Upgrade plan"
                                                imageUrl={getCTAUpgradeSettingsImageUrl('clinic', isMobile)}
                                                isMobile={isMobile}
                                            />
                                        )}

                                    {!showClinicSettings && (
                                        <>
                                            {clinicianData?.subscriptionData?.stripeCustomerId && (
                                                <Box
                                                    ref={(node) => {
                                                        sectionRefs.current['Billing'] = node;
                                                    }}
                                                >
                                                    <SettingsBilling />
                                                </Box>
                                            )}

                                            <Box
                                                ref={(node) => {
                                                    sectionRefs.current['Subscription'] = node;
                                                }}
                                            >
                                                <SettingsSubscription
                                                    plan={clinicianData?.subscriptionData?.accountType}
                                                />
                                            </Box>

                                            <SettingsDeleteAccount />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <ConfirmationDialog
                        open={isVerificationDialogOpen}
                        onConfirm={() => verifyEmailWithCode(verificationCode)}
                        handleClose={() => setIsVerificationDialogOpen(false)}
                        title="Verify new email"
                        message="A verification code has been sent to your new email. Please enter the code below to verify your email change."
                        confirmText="Verify"
                        cancelText="Cancel"
                        showTextField={true}
                        textFieldLabel="Verification Code"
                        textFieldValue={verificationCode}
                        onTextFieldChange={(e) => setVerificationCode(e.target.value)}
                    />

                    <ConfirmationDialog
                        open={isErrorDialogOpen}
                        onConfirm={() => setIsErrorDialogOpen(false)}
                        handleClose={() => setIsErrorDialogOpen(false)}
                        title="Email error"
                        message={errorDialogMessage}
                        confirmText="OK"
                        cancelText={null}
                        cancelTextReq={false}
                    />

                    <SuccessDialog
                        open={isSuccessEmailDialogOpen}
                        handleClose={() => setIsSuccessEmailDialogOpen(false)}
                        title="Email updated"
                        message="Your email has been successfully changed!"
                    />
                </Box>
            )}
            <UnsavedChangesModal
                open={showPrompt}
                onDiscardChanges={onConfirmNavigation}
                onCancel={onCancelNavigation}
            />
            <ConfirmationDialog
                open={isDeleteLogoDialogOpen}
                handleClose={handleCloseDeleteLogoDialog}
                onConfirm={removeClinicImage}
                title="Confirm removal"
                message={`Are you sure you want to remove the ${practiceOrClinicText} logo?`}
            />
            <CTAUpgradeDialog
                open={upgradeCTAOpenProfessional}
                handleClose={() => setUpgradeCTAOpenProfessional(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock unlimited sharing and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts',
                    'Unlimited digital sharing of all anatomy diagrams',
                    'Personalise with your clinic details',
                ]}
                imageUrl={getCTAUpgradeImageUrl('professional')}
            />
            <CTAUpgradeDialog
                open={upgradeCTAOpenClinic}
                handleClose={() => setUpgradeCTAOpenClinic(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />
        </CenteredWrapper>
    );
}
